/**
 * /embudos/:id/editar — editor del embudo.
 *
 * Pantalla propia y no modal: la rejilla de etapas (orden · nombre · tono ·
 * resultado · borrar) no entra en --evo-dialog-w sin apretarla hasta volverla
 * ilegible.
 *
 * De qué formulario come el embudo NO se configura acá: eso vive en el
 * formulario (`forms.funnelId`), y el mismo vínculo en dos pantallas es una
 * contradicción esperando ocurrir.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type { FunnelStageTone, FunnelStageOutcome } from '@evo-hub/shared';
import { Icon } from '../components/Icon.js';
import { Input } from '../components/ui/Input.js';
import { Button } from '../components/ui/Button.js';
import { IconButton } from '../components/ui/IconButton.js';
import { Dropdown } from '../components/ui/Dropdown.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { STAGE_TONE, padCount } from '../components/ui/Board.js';
import { useToast } from '../components/ui/Toast.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import {
  useFunnel, useReplaceStages, useUpdateFunnel,
  type StageInput,
} from '../hooks/useFunnels.js';

const GRID = '44px minmax(0, 1fr) 150px 150px 32px';

const TONES: FunnelStageTone[] = ['neutral', 'info', 'action', 'progress', 'won', 'lost'];

function CardShell({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-md)', overflow: 'hidden' }}>
      <div
        style={{
          padding: 'var(--evo-card-head-pad)',
          background: 'var(--evo-bg-2)',
          borderBottom: '1px solid var(--evo-stroke)',
          display: 'flex', alignItems: 'center', gap: 'var(--evo-card-head-gap)',
        }}
      >
        <span
          style={{
            flex: 1, minWidth: 0,
            fontSize: 'var(--evo-fs-caption)',
            fontWeight: 'var(--evo-list-header-fw)',
            letterSpacing: 'var(--evo-ls-caps)',
            textTransform: 'uppercase',
            color: 'var(--evo-fg-3)',
          }}
        >
          {title}
        </span>
        {right}
      </div>
      {children}
    </div>
  );
}

export function FunnelEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const funnelId = Number(id);

  const { setOverride } = useTopBarCtx();
  const { data: funnel, isLoading } = useFunnel(funnelId);
  const updateFunnel = useUpdateFunnel();
  const replaceStages = useReplaceStages();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [stages, setStages] = useState<StageInput[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setOverride({ title: t('funnels.pageTitle'), heroIcon: 'funnel', heroIconColor: 'var(--evo-cyan)' });
    return () => setOverride({});
  }, [setOverride, t]);

  useEffect(() => {
    if (!funnel) return;
    setName(funnel.name);
    setDescription(funnel.description ?? '');
    setStages(funnel.stages.map(s => ({ id: s.id, name: s.name, tone: s.tone, outcome: s.outcome })));
  }, [funnel]);

  function patchStage(index: number, patch: Partial<StageInput>) {
    setStages(prev => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    setSaved(false);
  }

  function addStage() {
    setStages(prev => [...prev, { name: '', tone: 'neutral', outcome: null }]);
    setSaved(false);
  }

  function removeStage(index: number) {
    setStages(prev => prev.filter((_, i) => i !== index));
    setSaved(false);
  }

  async function save() {
    try {
      await updateFunnel.mutateAsync({ id: funnelId, name: name.trim(), description: description.trim() || null });
      await replaceStages.mutateAsync({ funnelId, stages });
      setSaved(true);
    } catch (err) {
      // El 409 del servidor trae el nombre de la etapa y cuántas cartas tiene:
      // se muestra tal cual, que es lo que dice qué hacer.
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  if (isLoading || !funnel) return <LoadingState />;

  const busy = updateFunnel.isPending || replaceStages.isPending;

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      <div
        style={{
          flexShrink: 0,
          height: 'var(--evo-toolbar-height)',
          boxSizing: 'border-box',
          padding: '0 var(--evo-content-pad-x) 0 var(--evo-page-pad-x)',
          display: 'flex', alignItems: 'center', gap: 'var(--evo-space-4)',
          borderBottom: '1px solid var(--evo-stroke)',
        }}
      >
        <Button variant="link" tone="cyan" size="sm" icon={<Icon name="chevron-left" size={14} />} onClick={() => navigate('/embudos')}>
          {t('funnels.editor.back')}
        </Button>
        <span style={{ width: '1px', height: 'var(--evo-divider-h)', background: 'var(--evo-stroke)', flexShrink: 0 }} />
        <span
          style={{
            flex: 1, minWidth: 0,
            fontSize: 'var(--evo-fs-caption)',
            fontWeight: 'var(--evo-list-header-fw)',
            letterSpacing: 'var(--evo-ls-caps)',
            textTransform: 'uppercase',
            color: 'var(--evo-fg-3)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {t('funnels.editor.title')} · {funnel.name}
        </span>
        {saved && (
          <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-saved-fg)' }}>
            <Icon name="check" size={11} />
            {t('funnels.editor.saved')}
          </span>
        )}
        <Button tone="cyan" size="sm" loading={busy} onClick={() => { void save(); }}>
          {t('common.save')}
        </Button>
      </div>

      <div
        style={{
          flex: 1, minHeight: 0,
          overflowY: 'auto',
          padding: 'var(--evo-space-5) var(--evo-content-pad-x) var(--evo-space-6) var(--evo-page-pad-x)',
          display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-5)',
        }}
      >
        <CardShell title={t('opportunities.field.funnel')}>
          <div style={{ padding: 'var(--evo-card-pad)', display: 'flex', gap: 'var(--evo-space-5)', flexWrap: 'wrap' }}>
            <div style={{ width: 'var(--evo-field-w-md)' }}>
              <Input
                label={t('funnels.editor.name')}
                value={name}
                onChange={v => { setName(v); setSaved(false); }}
                fullWidth
              />
            </div>
            <div style={{ flex: 1, minWidth: 0, maxWidth: 'var(--evo-field-max-w)' }}>
              <Input
                label={t('funnels.editor.description')}
                value={description}
                onChange={v => { setDescription(v); setSaved(false); }}
                fullWidth
              />
            </div>
          </div>
        </CardShell>

        <CardShell
          title={t('funnels.editor.stages')}
          right={
            <>
              <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)' }}>
                {t('funnels.editor.stagesHint', { count: stages.length })}
              </span>
              <Button variant="link" tone="cyan" size="sm" onClick={addStage}>
                {t('funnels.editor.addStage')}
              </Button>
            </>
          }
        >
          <div
            style={{
              display: 'grid', gridTemplateColumns: GRID, alignItems: 'center',
              padding: 'var(--evo-table-head-py) var(--evo-space-4)',
              borderBottom: '1px solid var(--evo-stroke)',
              columnGap: 'var(--evo-space-3)',
            }}
          >
            {[
              t('funnels.editor.colOrder'), t('funnels.editor.colName'),
              t('funnels.editor.colTone'), t('funnels.editor.colOutcome'),
            ].map(label => (
              <span
                key={label}
                style={{
                  fontSize: 'var(--evo-list-header-fs)', fontWeight: 500,
                  textTransform: 'uppercase', letterSpacing: 'var(--evo-ls-caps)',
                  color: 'var(--evo-fg-3)',
                }}
              >
                {label}
              </span>
            ))}
            <span />
          </div>

          {stages.map((stage, index) => (
            <div
              key={stage.id ?? `nueva-${index}`}
              style={{
                display: 'grid', gridTemplateColumns: GRID, alignItems: 'center',
                padding: 'var(--evo-space-3) var(--evo-space-4)',
                borderBottom: '1px solid var(--evo-stroke)',
                columnGap: 'var(--evo-space-3)',
              }}
            >
              <span
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 'var(--evo-step-chip-sz)', height: 'var(--evo-step-chip-sz)',
                  borderRadius: 'var(--evo-checkbox-r)',
                  border: '1px solid var(--evo-stroke)',
                  fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-3)',
                  boxSizing: 'border-box',
                }}
              >
                {padCount(index + 1)}
              </span>

              <Input value={stage.name} onChange={v => patchStage(index, { name: v })} fullWidth />

              <Dropdown<FunnelStageTone>
                value={stage.tone}
                options={TONES.map(tone => ({
                  value: tone,
                  label: t(`funnels.editor.tone.${tone}`),
                  icon: (
                    <span style={{ width: '8px', height: '8px', borderRadius: 'var(--evo-r-pill)', background: STAGE_TONE[tone], flexShrink: 0 }} />
                  ),
                }))}
                onChange={tone => patchStage(index, { tone })}
                size="sm"
                triggerStyle={{ width: '100%' }}
              />

              <Dropdown<string>
                value={stage.outcome ?? ''}
                options={[
                  { value: '', label: t('funnels.editor.outcome.none') },
                  { value: 'won', label: t('funnels.editor.outcome.won') },
                  { value: 'lost', label: t('funnels.editor.outcome.lost') },
                ]}
                onChange={v => patchStage(index, { outcome: (v || null) as FunnelStageOutcome | null })}
                size="sm"
                triggerStyle={{ width: '100%' }}
              />

              <IconButton
                icon={<Icon name="trash" size={13} />}
                ariaLabel={t('common.delete')}
                variant="ghost"
                tone="danger"
                size="sm"
                onClick={() => removeStage(index)}
              />
            </div>
          ))}
        </CardShell>

        <div
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-3)',
            padding: 'var(--evo-space-4)',
            border: '1px solid var(--evo-stroke)',
            borderRadius: 'var(--evo-r-md)',
            background: 'var(--evo-fill-subtle)',
          }}
        >
          <span style={{ flexShrink: 0, display: 'flex', color: 'var(--evo-fg-3)', paddingTop: '1px' }}>
            <Icon name="alert" size={14} />
          </span>
          <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', lineHeight: 1.6 }}>
            {t('funnels.editor.entryHint')}
            {' '}
            {t('funnels.editor.deleteBlocked')}
          </p>
        </div>
      </div>
    </div>
  );
}
